export interface AsyncSemaphoreLease {
	release(): Promise<void>
}

interface AsyncSemaphoreWaiter {
	resolve: (lease: AsyncSemaphoreLease) => void
	reject: (reason: unknown) => void
	signal?: AbortSignal
	onAbort?: () => void
}

function getAbortReason(signal: AbortSignal): unknown {
	const reason: unknown = signal.reason
	return reason ?? new DOMException("The operation was aborted", "AbortError")
}

/**
 * FIFO async semaphore with abortable acquisition and idempotent leases.
 *
 * Callers should acquire before allocating the resource being bounded and hold
 * the returned lease until that resource has been fully consumed.
 */
export class AsyncSemaphore {
	private availablePermits: number
	private readonly waiters: AsyncSemaphoreWaiter[] = []

	constructor(public readonly capacity: number) {
		if (!Number.isSafeInteger(capacity) || capacity < 1) {
			throw new RangeError("Async semaphore capacity must be a positive safe integer")
		}

		this.availablePermits = capacity
	}

	get activeCount() {
		return this.capacity - this.availablePermits
	}

	get waitingCount() {
		return this.waiters.length
	}

	async acquire(signal?: AbortSignal): Promise<AsyncSemaphoreLease> {
		signal?.throwIfAborted()

		if (this.availablePermits > 0) {
			this.availablePermits--
			return this.createLease()
		}

		return await new Promise<AsyncSemaphoreLease>((resolve, reject) => {
			const waiter: AsyncSemaphoreWaiter = { resolve, reject, signal }

			if (signal) {
				waiter.onAbort = () => {
					const index = this.waiters.indexOf(waiter)
					if (index < 0) {
						return
					}

					this.waiters.splice(index, 1)
					reject(getAbortReason(signal))
				}
				signal.addEventListener("abort", waiter.onAbort, { once: true })
			}

			this.waiters.push(waiter)
		})
	}

	private createLease(): AsyncSemaphoreLease {
		let released = false

		return {
			release: () => {
				if (released) {
					return Promise.resolve()
				}

				released = true
				this.releasePermit()
				return Promise.resolve()
			},
		}
	}

	private releasePermit() {
		const waiter = this.waiters.shift()
		if (waiter) {
			if (waiter.signal && waiter.onAbort) {
				waiter.signal.removeEventListener("abort", waiter.onAbort)
			}
			waiter.resolve(this.createLease())
			return
		}

		if (this.availablePermits >= this.capacity) {
			throw new Error("Async semaphore released more permits than it acquired")
		}

		this.availablePermits++
	}
}
